/**
 * Outdoor space (版位) operational health state.
 * Source: `health_states` field on the outdoor-ad/spaces-detail API response.
 */
export enum SpaceHealthState {
  BANNED = 'banned',
  WARMING_UP = 'warming_up',
  NO_DATA = 'no_data',
  NORMAL = 'normal'
}

/**
 * ECOPD-5564：SSP 新增、但 PMax 一律呈現為「正常」的健康狀態。
 * 值為 wire 格式（UPPERCASE），比對時與 parseSpaceHealthState 一樣先 lower-case。
 *
 * 這裡是「忽略」而不是「別名成 NORMAL」—— 別名會讓 NORMAL（聚合優先序 1，最優先
 * 勝出）蓋掉併存的既有異常狀態，例如 ["NO_DATA","LOW_OPERATING_HOURS"] 會變成正常，
 * 把「訊號異常」chip 吃掉。忽略則讓它退出優先序競爭，只有整組狀態都被忽略時才會
 * 落到 utils/spaceHealth.ts 的 `?? NORMAL`。
 */
export const IGNORED_SPACE_HEALTH_STATES = [
  'LOW_OPERATING_HOURS',
  'HEALTH_LEVEL_CRITICAL'
] as const;

/**
 * 顯示嚴重度 — 數字越小越嚴重，用於「chip 清單排序」，讓 banned / no_data
 * 等異常版位浮到最前面吸引注意（見 Tags.tsx）。這與下方的聚合優先順序方向
 * 相反，兩者用途不同，請勿混用。
 */
export const SPACE_HEALTH_SEVERITY: Record<SpaceHealthState, number> = {
  [SpaceHealthState.BANNED]: 1,
  [SpaceHealthState.WARMING_UP]: 2,
  [SpaceHealthState.NO_DATA]: 3,
  [SpaceHealthState.NORMAL]: 4
};

/**
 * 聚合匹配優先順序 — 數字越小越優先勝出，用於把「多筆版位資料」歸一成單一
 * 狀態（一個門市對應多個版位，或單一版位的 healthStates[] 陣列）。
 * ON（正常）最先勝出，BANNED（停止服務）最後 —— 只要有一筆還在正常服務就
 * 呈現 ON，全部都停了才顯示 BANNED（ECOPD-5225：先對到 ON，最後才是 BANNED）。
 */
export const SPACE_HEALTH_MATCH_PRIORITY: Record<SpaceHealthState, number> = {
  [SpaceHealthState.NORMAL]: 1,
  [SpaceHealthState.WARMING_UP]: 2,
  [SpaceHealthState.NO_DATA]: 3,
  [SpaceHealthState.BANNED]: 4
};

/** i18n key for the status name shown in the chip tooltip header. */
export const SPACE_HEALTH_STATUS_I18N: Record<SpaceHealthState, string> = {
  [SpaceHealthState.BANNED]: 'spaceHealth.labels.banned',
  [SpaceHealthState.WARMING_UP]: 'spaceHealth.labels.warmingUp',
  [SpaceHealthState.NO_DATA]: 'spaceHealth.labels.noData',
  [SpaceHealthState.NORMAL]: 'spaceHealth.labels.normal'
};

/** ECOPD-5564：這個 wire 值是否為刻意忽略（呈現為正常）的狀態。 */
export function isIgnoredSpaceHealthState(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  const normalized = raw.toLowerCase();
  return IGNORED_SPACE_HEALTH_STATES.some(
    (s) => s.toLowerCase() === normalized
  );
}

/** Parse a single raw API value into a SpaceHealthState, or undefined when unknown. */
export function parseSpaceHealthState(
  raw: unknown
): SpaceHealthState | undefined {
  if (typeof raw !== 'string') return undefined;
  const normalized = raw.toLowerCase();
  // ECOPD-5564：先擋在 enum 比對之前，日後有人把這些值加進 enum 也仍然有效。
  if (isIgnoredSpaceHealthState(normalized)) return undefined;
  const match = Object.values(SpaceHealthState).find((s) => s === normalized);
  return match as SpaceHealthState | undefined;
}

/**
 * The backend sends `healthStates` as an array (e.g. `["NO_DATA"]`) with
 * UPPERCASE values. Reduce it to a single state by aggregation match priority
 * (ON/normal wins first, BANNED last — 見 SPACE_HEALTH_MATCH_PRIORITY)，
 * or undefined when none are recognized.
 */
export function parseAggregatedHealthState(
  raw: unknown
): SpaceHealthState | undefined {
  const list = Array.isArray(raw) ? raw : [raw];
  let best: SpaceHealthState | undefined;
  let bestPriority = Number.POSITIVE_INFINITY;
  for (const item of list) {
    const state = parseSpaceHealthState(item);
    if (state && SPACE_HEALTH_MATCH_PRIORITY[state] < bestPriority) {
      bestPriority = SPACE_HEALTH_MATCH_PRIORITY[state];
      best = state;
    }
  }
  return best;
}
